import { TicketCategory } from 'Interfaces/SupportTickets/supportTicketInterfaces';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { rootStore } from 'stores/RootStore';
import { captureComprehensiveTicketData } from 'utils/captureTicketData';
import styles from './supportTicketChat.module.scss';

// Helper function to detect environment from URL
const getEnvironment = (): string => {
  const origin = window.location.origin;
  if (origin.includes('localhost')) return 'local';
  if (origin.includes('dev')) return 'dev';
  if (origin.includes('staging')) return 'staging';
  if (origin.includes('qa')) return 'qa';
  return 'production';
};

export const SupportTicketChat: React.FC = observer(() => {
  const { SupportTicketStore, UserStore } = rootStore;
  const [isClosing, setIsClosing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<TicketCategory | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<{ category?: string; title?: string; description?: string }>(
    {}
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pageRoute = window.location.pathname;
  const environment = getEnvironment();

  // Load persisted data when panel opens
  React.useEffect(() => {
    if (SupportTicketStore.isChatOpen && SupportTicketStore.formData) {
      setSelectedCategory(SupportTicketStore.formData.selectedCategory);
      setTitle(SupportTicketStore.formData.title);
      setDescription(SupportTicketStore.formData.description);
      setAttachments(SupportTicketStore.formData.attachments);
    }
  }, [SupportTicketStore.isChatOpen, SupportTicketStore.formData]);

  // Persist data to store on change
  React.useEffect(() => {
    if (SupportTicketStore.isChatOpen) {
      SupportTicketStore.formData = {
        selectedCategory,
        title,
        description,
        attachments,
      };
    }
  }, [SupportTicketStore.isChatOpen, SupportTicketStore, selectedCategory, title, description, attachments]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      SupportTicketStore.closeChat();
      setIsClosing(false);
    }, 250);
  };

  const validateForm = (): boolean => {
    const newErrors: { category?: string; title?: string; description?: string } = {};

    if (!selectedCategory) {
      newErrors.category = 'Please select a category';
    }

    if (!title || title.trim().length === 0) {
      newErrors.title = 'Please enter a title';
    } else if (title.length > 255) {
      newErrors.title = 'Title must be less than 255 characters';
    }

    if (!description || description.trim().length === 0) {
      newErrors.description = 'Please enter a description';
    } else if (description.length > 2000) {
      newErrors.description = 'Description must be less than 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const submittingToast = toast.info('Submitting ticket...', {
      autoClose: false,
      closeButton: false,
    });

    try {
      // Capture comprehensive data
      const { browserInfo, additionalContext } = await captureComprehensiveTicketData(
        {
          'Page Title': document.title,
          'User Role': UserStore.userRole || 'N/A',
          'Session Active': 'Yes',
        },
        false
      );

      // Create ticket
      const result = await SupportTicketStore.createJSMTicket({
        categoryId: selectedCategory!.id,
        title,
        description,
        pageRoute,
        environment,
        priority: selectedCategory?.category_code === 'UI_BUG' ? 'High' : 'Medium',
        pageUrl: window.location.href,
        screenResolution: `${screen.width}x${screen.height}`,
        sessionId: sessionStorage.getItem('sessionId') || undefined,
        userRole: UserStore.userRole || undefined,
        browserInfo,
        additionalContext,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      toast.dismiss(submittingToast);

      // Show success
      if (result) {
        toast.success(
          <div>
            <strong>Ticket Created Successfully!</strong>
            <div style={{ marginTop: '8px' }}>
              <div>
                Ticket ID: <strong>{result.ticket.jiraTicketId}</strong>
              </div>
              {result.ticket.jiraUrl && (
                <a
                  href={result.ticket.jiraUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#4CAF50', textDecoration: 'underline' }}
                >
                  View in Jira →
                </a>
              )}
            </div>
          </div>,
          { autoClose: 8000 }
        );
      }

      // Reset form and close
      setSelectedCategory(null);
      setTitle('');
      setDescription('');
      setAttachments([]);
      setErrors({});
      SupportTicketStore.resetChat();
      handleClose();
    } catch (error) {
      console.error('Failed to create ticket', error);
      toast.dismiss(submittingToast);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.warning(`File ${file.name} exceeds 5MB limit`);
          return false;
        }
        return true;
      });
      setAttachments(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  if (!SupportTicketStore.isChatOpen) return null;

  return (
    <div className={`${styles.formPanel} ${isClosing ? styles.formPanelClosing : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <i className="pi pi-comments" />
          <h3>Report an Issue</h3>
        </div>
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close"
          type="button"
        >
          <i className="pi pi-times" />
        </button>
      </div>

      {/* Form Content */}
      <div className={styles.formContent}>
        {/* Category */}
        <div className={styles.formField}>
          <label htmlFor="category" className={styles.label}>
            Category <span className={styles.required}>*</span>
          </label>
          <Dropdown
            id="category"
            value={selectedCategory}
            options={SupportTicketStore.categories}
            onChange={e => {
              setSelectedCategory(e.value);
              setErrors(prev => ({ ...prev, category: undefined }));
            }}
            optionLabel="category_name"
            placeholder="Select issue category"
            className={styles.dropdown}
            loading={SupportTicketStore.isCategoriesLoading}
          />
          {errors.category && <small className={styles.error}>{errors.category}</small>}
        </div>

        {/* Title */}
        <div className={styles.formField}>
          <label htmlFor="title" className={styles.label}>
            Title <span className={styles.required}>*</span>
          </label>
          <InputText
            id="title"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
              setErrors(prev => ({ ...prev, title: undefined }));
            }}
            placeholder="Brief summary of the issue..."
            className={styles.input}
            maxLength={255}
          />
          <div className={styles.charCount}>{title.length} / 255</div>
          {errors.title && <small className={styles.error}>{errors.title}</small>}
        </div>

        {/* Description */}
        <div className={styles.formField}>
          <label htmlFor="description" className={styles.label}>
            Description <span className={styles.required}>*</span>
          </label>
          <InputTextarea
            id="description"
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              setErrors(prev => ({ ...prev, description: undefined }));
            }}
            rows={5}
            placeholder="Please describe the issue in detail..."
            className={styles.textarea}
            maxLength={2000}
          />
          <div className={styles.charCount}>{description.length} / 2000</div>
          {errors.description && <small className={styles.error}>{errors.description}</small>}
        </div>

        {/* Attachments */}
        <div className={styles.formField}>
          <label className={styles.label}>Attachments (Optional)</label>
          <div className={styles.fileUploadArea}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className={styles.fileInput}
              id="file-upload"
            />
            <label htmlFor="file-upload" className={styles.uploadButton}>
              <i className="pi pi-paperclip" />
              <span>Choose Files</span>
            </label>
            <span className={styles.fileHint}>Max 5MB per file</span>
          </div>

          {attachments.length > 0 && (
            <div className={styles.fileList}>
              {attachments.map((file, index) => (
                <div key={index} className={styles.fileItem}>
                  <span className={styles.fileName}>{file.name}</span>
                  <button
                    type="button"
                    className={styles.removeFileButton}
                    onClick={() => handleRemoveFile(index)}
                  >
                    <i className="pi pi-times" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Auto-populated Info */}
        <div className={styles.autoInfo}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Page:</span>
            <span className={styles.infoValue}>{pageRoute}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Environment:</span>
            <Tag
              value={environment}
              severity={
                environment === 'production'
                  ? 'danger'
                  : environment === 'staging'
                    ? 'warning'
                    : 'info'
              }
            />
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className={styles.footer}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={handleClose}
          className={styles.cancelButton}
          outlined
        />
        <Button
          label="Submit Ticket"
          icon="pi pi-check"
          onClick={handleSubmit}
          className={styles.submitButton}
          loading={SupportTicketStore.isCreatingTicket}
          disabled={SupportTicketStore.isCreatingTicket}
        />
      </div>
    </div>
  );
});
